import React, {useMemo} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import StepConfigEditorRouter from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouter';
import {makeInitialConfigStateFromConfigShape} from 'pipelines/generalized-step-config-editor/initial-values/makeInitialConfigState';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

// TODO(BNCH-132431): Backfill missing variable names
const SYSTEM_VARIABLE_TO_NAME: Record<string, string> = {
  datasetName: 'Dataset name',
  documentId: 'Entry or worksheet',
  folderId: 'Folder',
  fileId: 'File',
};

function getVariableName(variable: AnalysisTemplateSelectorTemplate['configVariables'][number]): string {
  if (variable.name.length > 0) {
    return variable.name;
  }
  return SYSTEM_VARIABLE_TO_NAME[variable.configVariableKey] ?? variable.configVariableKey;
}

function adjustVariableTypeConfigSpec(configSpec: PipelineConfigJsonSpec): PipelineConfigJsonSpec {
  // 'DOCUMENT' is not a searchable type; users should only be able to search for 'ENTRY' and 'WORKSHEET'
  return configSpec.kind === 'domain'
    ? {...configSpec, types: configSpec.types.filter((type) => type !== 'DOCUMENT')}
    : configSpec;
}

interface AnalysisTemplateVariablesFormProps {
  clientStateId: string;
  variables: AnalysisTemplateSelectorTemplate['configVariables'];
  variableBindings: Record<string, JSONValue>;
  onVariableBindingsChange: (bindings: Record<string, JSONValue>) => void;
}

export default function AnalysisTemplateVariablesForm({
  clientStateId,
  variables,
  variableBindings,
  onVariableBindingsChange,
}: AnalysisTemplateVariablesFormProps): JSX.Element {
  const configShape = useMemo(
    () => ({
      kind: 'shape' as 'shape',
      required: true,
      fields: Object.fromEntries(
        variables.map((variable) => [
          variable.id,
          adjustVariableTypeConfigSpec(variable.variableTypeConfigSpec),
        ])
      ),
    }),
    [variables]
  );

  const uiSchema = useMemo(
    () =>
      Object.fromEntries(
        variables.map((variable) => [variable.id, {displayName: getVariableName(variable)}])
      ),
    [variables]
  );

  const emptyVariableBindings = useMemo(
    () => makeInitialConfigStateFromConfigShape(configShape.fields, uiSchema, false),
    [configShape, uiSchema]
  );

  const editorContext = useMemo(() => ({dataFramesInfos: {}, clientStateId}), [clientStateId]);

  return (
    <StepConfigEditorContext.Provider value={editorContext}>
      <StepConfigEditorRouter
        configShape={configShape}
        config={{...emptyVariableBindings, ...variableBindings}}
        onChangeConfig={onVariableBindingsChange}
        uiSchema={uiSchema}
        isTopLevel={false}
        forceSingleColumnDisplay={false}
        dataTestAttrs={{}}
      />
    </StepConfigEditorContext.Provider>
  );
}
